// @ts-check

import React, { useEffect } from "react";

import { AccountConnection, Link, Text } from "@shopify/polaris";

import { Platform } from "components/Platform/Platform";

import { useGlobalContext } from "context/GlobalContext";
import { useSettings } from "context/SettingsContext";
import { usePlatforms } from "context/PlatformsContext";

import { getPlatformConnectionUrl } from "utils/platform-connection";

const PLATFORM_TERMS = {
  facebook: (
    <Text as="p" variant="bodyMd">
      By clicking <Text as="span" variant="bodySm" fontWeight="semibold">Connect</Text>, you agree to the <Link target="_blank" url="https://www.facebook.com/terms.php">Facebook Terms of Service</Link>.
    </Text>
  ),
};

const CONNECTION_STATUS_LABELS = {
  OK: "Connected",
  ERROR: "Connected",
  // ERROR: "Error connecting, please try again or contact support",
  PENDING: "Pending setup...",
  SYNCING: "Syncing information from your account...",
};

export const ExternalPlatformConnection = ({ platform }) => {
  const { store, isFetchingStore } = useGlobalContext();
  const { setError } = useSettings();
  const {
    connectablePlatformsLoading,
    setConnectablePlatformsLoading,
    refetchConnectablePlatforms,
    isFetchingConnectablePlatforms,
  } = usePlatforms();

  const connected = platform.connected_platforms.length > 0;
  const connectionStatus = platform.connected_platforms?.[0]?.status;
  const loading = connectablePlatformsLoading.includes(platform.id) || isFetchingConnectablePlatforms || isFetchingStore;

  useEffect(() => {
    return () => {
      setConnectablePlatformsLoading([]);
    };
  }, [setError, setConnectablePlatformsLoading]);

  const handleConnect = () => {
    setError(null);
    setConnectablePlatformsLoading(loading => [...loading, platform.id]);

    const redirectUrl = getPlatformConnectionUrl(platform, store.shop, { redirectUrl: "/settings" });
    open(redirectUrl, "_blank");
  };

  const handleDisconnect = () => {
    setError(null);
    setConnectablePlatformsLoading(loading => [...loading, platform.id]);

    return fetch(`/api/shop/connected-platforms/${platform.connected_platforms[0].id}`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.error) {
          throw new Error(data.error);
        }

        if (data.success) {
          refetchConnectablePlatforms();
        }
      })
      .catch(() => {
        setError("There was an error disconnecting your account.");
      })
      .finally(() => {
        setConnectablePlatformsLoading(loading => loading.filter(id => id !== platform.id));
      });
  };

  const title = (
    <Platform
      platformKey={platform.key}
      platformName={platform.name}
      fontWeight="semibold"
    />
  );

  let details;
  if (connected) {
    details = CONNECTION_STATUS_LABELS[connectionStatus];
  } else {
    details = "No account connected";
  }

  let action = {
    content: "Connect",
    onAction: handleConnect,
    loading: loading,
  };
  if (connected) {
    action = {
      content: "Disconnect",
      onAction: handleDisconnect,
      loading: loading,
    };
    // if (connectionStatus === "ERROR") {
    //   action = {
    //     content: "Re-connect",
    //     onAction: handleConnect,
    //     loading: loading,
    //     tone: "critical",
    //   };
    // } else {
    //   action = {
    //     content: "Disconnect",
    //     onAction: handleDisconnect,
    //     loading: loading,
    //   };
    // }
  }

  return (
    <AccountConnection
      key={platform.id}
      title={title}
      accountName={platform.name}
      connected={connected}
      action={action}
      details={details}
      termsOfService={connected ? undefined : PLATFORM_TERMS[platform.key]}
    />
  );
};
